import {useIntl} from 'react-intl';
import {useEffect} from 'react';
import {Tooltip} from 'react-tippy';
import {ReactComponent as TooltipIcon} from 'assets/icons/tooltip.svg';
import {formDataLayerHelper} from 'utils/dataLayerHelper';

const RadioButtons = ({settings, onChange, setState}) => {
  const intl = useIntl();
  const {
    name,
    state,
    options,
    wrapperClasses,
    disabled = false,
    label,
    labelTooltip,
  } = settings || {};
  const dataLayerCallback = formDataLayerHelper(intl, settings);

  useEffect(() => {
    options.forEach((option) => {
      if (option.checked) {
        setState((prev) => {
          return {
            ...prev,
            [name]: option.value,
          }
        });
      }
    });
  }, []);

  // Watch for external state changes (like when clear filters is clicked)
  useEffect(() => {
    // If state is explicitly set to empty string but current value isn't empty, update it
    if (state[name] === '' || state[name] === undefined) {
      const anyOption = options.find(opt => opt.value === '');
      if (anyOption && state[name] === undefined) {
        setState((prev) => ({
          ...prev,
          [name]: '',
        }));
      }
    }
  }, [state[name], options]);

  if (!name || !options) {
    return '';
  }

  const renderRadios = () => {
    return options.map((option) => {
      const {label: optionLabel, value} = option;
      // Use String() to ensure both sides are strings for comparison
      const currentValue = String(state[name] ?? '');
      const optionValue = String(value ?? '');
      const isChecked = currentValue === optionValue;
      const classes = `form-item form-item--radio ${isChecked ? 'form-item--radio-active' : ''} ${disabled ? 'form-disabled' : ''}`;

      return (
        <div className={classes} key={`option-${value || 'empty'}`}>
          <input className="form-radio"
                 name={name}
                 type="radio"
                 checked={isChecked}
                 value={value}
                 disabled={disabled}
                 onChange={(event) => {
                   onChange(event);
                   if (typeof dataLayerCallback === 'function') {
                     dataLayerCallback(event);
                   }
                 }}
                 id={`${name}-${value || 'empty'}`}
          />
          <label className="option" htmlFor={`${name}-${value || 'empty'}`}>
            {intl.formatMessage({id: optionLabel})}
          </label>
        </div>
      );
    });
  };

  const renderLabel = () => {
    if (!label) {
      return '';
    }

    return (
      <>
        <label className="checkboxes-label">
          {intl.formatMessage({id: label})}
          {labelTooltip ?
            <Tooltip title={intl.formatMessage({id: labelTooltip})}
                     className="ml-10"
                     trigger="mouseenter focus click"
                     arrow={true}
                     tabIndex={0}
                     inertia={true}
                     interactive={true}
                     style={{display: 'inline-block'}}>
              <TooltipIcon/>
            </Tooltip> : ''
          }
        </label>
      </>
    );
  };

  return (
    <div className={wrapperClasses}>
      {renderLabel()}
      {renderRadios()}
    </div>
  );
};

export default RadioButtons;
